import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  mapCustomPageRow,
  type MerchantCustomPage,
  type MerchantCustomPageInput,
} from '@/lib/customPages';

const TABLE = 'merchant_custom_pages';

interface UseCustomPagesResult {
  pages: MerchantCustomPage[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (input: MerchantCustomPageInput) => Promise<MerchantCustomPage>;
  update: (id: number, input: MerchantCustomPageInput) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

/** CRUD des pages personnalisées d'une boutique (scopé par idcommerce). */
export function useCustomPages(merchantId: number | null): UseCustomPagesResult {
  const [pages, setPages] = useState<MerchantCustomPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!merchantId) {
      setPages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from(TABLE)
        .select('*')
        .eq('idcommerce', merchantId)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setPages((data || []).map((row) => mapCustomPageRow(row as Record<string, unknown>)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les pages.');
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (input: MerchantCustomPageInput): Promise<MerchantCustomPage> => {
      if (!merchantId) throw new Error('Marchand introuvable.');
      const { data, error: insertError } = await supabase
        .from(TABLE)
        .insert({
          idcommerce: merchantId,
          title: input.title.trim(),
          slug: input.slug.trim(),
          content: input.content,
          published: input.published,
        })
        .select('*')
        .single();
      if (insertError) throw insertError;
      await refresh();
      return mapCustomPageRow(data as Record<string, unknown>);
    },
    [merchantId, refresh],
  );

  const update = useCallback(
    async (id: number, input: MerchantCustomPageInput) => {
      const { error: updateError } = await supabase
        .from(TABLE)
        .update({
          title: input.title.trim(),
          slug: input.slug.trim(),
          content: input.content,
          published: input.published,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (updateError) throw updateError;
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: number) => {
      const { error: deleteError } = await supabase.from(TABLE).delete().eq('id', id);
      if (deleteError) throw deleteError;
      await refresh();
    },
    [refresh],
  );

  return { pages, loading, error, refresh, create, update, remove };
}

/** Charge une page publique par son slug (ou une page non publiée en aperçu). */
export async function fetchPublicCustomPage(
  merchantId: number,
  slug: string,
): Promise<MerchantCustomPage | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('idcommerce', merchantId)
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return data ? mapCustomPageRow(data as Record<string, unknown>) : null;
}

/** Liste les pages PUBLIÉES d'une boutique (pour la navigation publique). */
export async function listPublishedCustomPages(
  merchantId: number,
): Promise<MerchantCustomPage[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('idcommerce', merchantId)
    .eq('published', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((row) => mapCustomPageRow(row as Record<string, unknown>));
}